import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useAddPermission,
  useAppPermissions,
  useRemovePermission,
} from '@/admin/useAdminApps';

export interface PermissionsPanelProps {
  appId: number;
}

export function PermissionsPanel({ appId }: PermissionsPanelProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useAppPermissions(appId);
  const add = useAddPermission(appId);
  const remove = useRemovePermission(appId);

  const [groupId, setGroupId] = useState('');

  function handleAdd(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!groupId.trim()) return;
    add.mutate(groupId.trim(), { onSuccess: () => setGroupId('') });
  }

  return (
    <div className="space-y-3 rounded-md border bg-slate-50 p-3">
      <h4 className="text-sm font-semibold text-appsol-900">
        {t('admin.permissions.title')}
      </h4>

      {isLoading && <p className="text-xs text-slate-500">{t('home.loading')}</p>}
      {isError && (
        <p role="alert" className="text-xs text-rose-700">
          {t('admin.permissions.error')}
        </p>
      )}

      {data && data.length === 0 && (
        <p className="text-xs italic text-slate-500">{t('admin.permissions.empty')}</p>
      )}

      {data && data.length > 0 && (
        <ul className="space-y-1">
          {data.map((perm) => (
            <li key={perm.id} className="flex items-center justify-between gap-2 text-sm">
              <code className="rounded bg-white px-2 py-0.5 text-xs">
                {perm.entra_group_id}
              </code>
              <button
                type="button"
                aria-label={t('admin.permissions.remove', { group: perm.entra_group_id })}
                onClick={() => remove.mutate(perm.id)}
                disabled={remove.isPending}
                className="text-xs text-rose-700 hover:underline disabled:opacity-50"
              >
                {t('admin.permissions.removeCta')}
              </button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={handleAdd} className="flex gap-2">
        <input
          aria-label={t('admin.permissions.groupId')}
          value={groupId}
          onChange={(e) => setGroupId(e.target.value)}
          placeholder={t('admin.permissions.groupIdPlaceholder')}
          className="flex-1 rounded border px-2 py-1 text-sm"
        />
        <button
          type="submit"
          disabled={add.isPending || !groupId.trim()}
          className="rounded-md bg-appsol-500 px-3 py-1 text-sm font-medium text-white hover:bg-appsol-900 disabled:opacity-50"
        >
          {t('admin.permissions.addCta')}
        </button>
      </form>
    </div>
  );
}
